// PettyCashPage — 小口現金出納帳 (2026-07-22)
//
// 運用: 各店が小口現金で経費支出 → 出金伝票+証憑を写真提出(金曜午前締め) → 仕訳 →
//       金曜に西武信金から出金し補充 (インプレスト方式)。
// 機能: ①店舗別出納帳+残高(定額−未補充支出) ②カメラ提出(書類補正つき)/スキャンPDFのD&D提出→miniPC AI分析
//       ③AI仕訳提案(発生日=証憑優先/取引先照合/T番号抽出/未登録取引先フラグ)
//       ④コメント・修正指示 ⑤週次出金額表+印刷 ⑥西武出金の自動突合で出金済みマーク
// freee 登録は「承認して登録」クリック起点 (R2)。取引先の自動作成はしない (R3)。

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container, PageTitle, CardBase, Button, SelectBox, MessageBlock, InlineSpinner, Breadcrumbs,
} from '@freee_jp/vibes';
import { MdRefresh, MdPrint, MdPhotoCamera, MdClose, MdContentCopy } from 'react-icons/md';
import QRCode from 'qrcode';
import {
  fetchSectionsMap, fetchAccountItemsMap, fetchTaxCodesMap,
  fetchItemsMap, fetchTagsMap, fetchAllFreeePartners, FreeePartnerLite,
  PettyCashEntry, PettyCashComment, PettyCashBook, PETTY_CASH_CHIEFS,
  submitPettyCashEntry, fetchPettyCashEntry, pettyCashImageUrl,
  postPettyCashComment, registerPettyCashEntry, rejectPettyCashEntry,
  manualEditPettyCashEntry,
  fetchPettyCashLedger, fetchPettyCashWeekly, fetchPettyCashWeeks,
  fetchPettyCashSettings, savePettyCashSetting, markPettyCashWeeklyWithdrawn,
  PettyCashLedgerResponse, PettyCashWeeklySection,
} from '../api/client';

// 帳簿メタ: petty_cash=小口現金 (インプレスト・西武突合) / chief_advance=室長立替金 (本部現金出金・手動マーク)
const BOOK_META: Record<PettyCashBook, { title: string; flow: string; kioskTitle: string; weeklyLabel: string }> = {
  petty_cash: {
    title: '小口現金出納帳',
    flow: '金曜午前締め → 当日 西武信金から出金・補充',
    kioskTitle: '小口現金 提出',
    weeklyLabel: '合計 (金曜出金額)',
  },
  chief_advance: {
    title: '室長立替金出納帳',
    flow: '金曜午前締め → 当日 本部現金から出金 (口座:現金・メモタグ 室長立替金+室長名)',
    kioskTitle: '室長立替金 提出',
    weeklyLabel: '合計 (金曜 本部現金から出金)',
  },
};

// ---------------------------------------------------------------------------
const INK = '#26231f'; const SUB = '#736d65'; const FAINT = '#c2bdb5';
const LINE = '#eeece9'; const RULE = '#ddd9d3'; const HEAD_BG = '#fbfaf8';
const ACCENT = '#2f5fd0'; const ACCENT_BG = '#eef3fc';
const GOOD = '#1c7c3c'; const GOOD_BG = '#eef8f0'; const WARN = '#c77700'; const WARN_BG = '#fdf3e4';
const NEG = '#c0392b';
const numCss: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };
const yen = (n: number | null | undefined) => (n == null ? '—' : `¥${Number(n).toLocaleString('ja-JP')}`);

// 店舗として扱う section (本部・販売課などは出納帳の主対象外だが選択は可能にする)
const STORE_FIRST_ORDER = [3068544, 3068545, 3068546, 3068547, 3068548, 3068549, 3068550, 3068551, 3068552, 3068553, 3068554, 3068540];

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  submitted:    { label: '提出済',     color: SUB,    bg: '#f1efec' },
  analyzing:    { label: 'AI分析中',   color: ACCENT, bg: ACCENT_BG },
  proposed:     { label: '仕訳提案',   color: '#6b21a8', bg: '#f5f0fb' },
  needs_review: { label: '要確認',     color: WARN,   bg: WARN_BG },
  approved:     { label: '承認済',     color: GOOD,   bg: GOOD_BG },
  registered:   { label: 'freee登録済', color: GOOD,  bg: GOOD_BG },
  rejected:     { label: '却下',       color: NEG,    bg: '#fdecea' },
};

// ---------------------------------------------------------------------------
// 書類補正: ①OpenCV.js で書類の四辺形を検出→射影変換でスキャン風に切り取り (image2scan 方式)
//          ②ヒストグラム引き伸ばし (2%/98% 点) で白背景・高コントラスト化。
// OpenCV (13MB) は撮影時に遅延ロード。検出失敗/未ロード時は②のみに自動フォールバック。
// ---------------------------------------------------------------------------
// script タグで /opencv.js (UMD → window.cv) を遅延ロード。
// dynamic import だと CRA webpack が emscripten glue の require('crypto') を解決できないため
// バンドル外 (public/) から読む。window.cv は Promise の場合と直接 module の場合がある。
let cvPromise: Promise<any> | null = null;
function loadCv(): Promise<any> {
  if (!cvPromise) {
    cvPromise = new Promise<any>((resolve) => {
      const w = window as any;
      const finish = (c: any) => resolve(c && c.Mat ? c : null);
      const settle = (c: any) => {
        if (!c) { resolve(null); return; }
        if (typeof c.then === 'function') { c.then(finish).catch(() => resolve(null)); return; }
        if (c.Mat) { finish(c); return; }
        try { c.onRuntimeInitialized = () => finish(c); } catch { /* noop */ }
        setTimeout(() => finish(w.cv), 15000);
      };
      if (w.cv) { settle(w.cv); return; }
      const s = document.createElement('script');
      s.src = `${process.env.PUBLIC_URL || ''}/opencv.js`;
      s.async = true;
      s.onload = () => settle(w.cv);
      s.onerror = () => resolve(null);
      document.head.appendChild(s);
    });
  }
  return cvPromise;
}

// 明部(紙)セグメンテーション検出 — レシート向け (2026-07-24)。
// エッジ検出(Canny)はレシートが小さい/背景コントラストが低いと外すため、
// 「紙=明るい連結領域」を Otsu 二値化で取り、最大輪郭を4角形近似 or 最小外接矩形で切る。
function brightPaperCorners(cv: any, src: any, imgArea: number): Array<{ x: number; y: number }> | null {
  const mats: any[] = [];
  const track = (m: any) => { mats.push(m); return m; };
  try {
    const gray = track(new cv.Mat());
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    const blur = track(new cv.Mat());
    cv.GaussianBlur(gray, blur, new cv.Size(5, 5), 0);
    const bin = track(new cv.Mat());
    cv.threshold(blur, bin, 0, 255, cv.THRESH_BINARY + cv.THRESH_OTSU);
    const kernel = track(cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(9, 9)));
    cv.morphologyEx(bin, bin, cv.MORPH_CLOSE, kernel);
    const contours = new cv.MatVector();
    const hier = track(new cv.Mat());
    cv.findContours(bin, contours, hier, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);
    let bestC: any = null; let bestArea = 0;
    for (let i = 0; i < contours.size(); i++) {
      const c = contours.get(i);
      const area = cv.contourArea(c);
      // レシートは画面の 5% 程度でも拾う。95% 超は「背景ごと白」= 切り取り無意味なので除外
      if (area >= imgArea * 0.05 && area <= imgArea * 0.95 && area > bestArea) {
        if (bestC) bestC.delete();
        bestC = c; bestArea = area;
      } else c.delete();
    }
    contours.delete();
    if (!bestC) return null;
    let pts: Array<{ x: number; y: number }> | null = null;
    const peri = cv.arcLength(bestC, true);
    const approx = track(new cv.Mat());
    cv.approxPolyDP(bestC, approx, 0.02 * peri, true);
    if (approx.rows === 4) {
      pts = [];
      for (let r = 0; r < 4; r++) pts.push({ x: approx.data32S[r * 2], y: approx.data32S[r * 2 + 1] });
    } else {
      // 湾曲レシート等: 最小外接回転矩形の4頂点で切る (角の推定より安全)。
      // 注意: この opencv.js ビルドの cv.boxPoints は空 Mat を返すバグがある (実測 2026-07-24
      // → NaN 変換で黒画像化)。cv.RotatedRect.points が正常動作するのでそちらを使う。
      const rect = cv.minAreaRect(bestC);
      if (rect.size.width * rect.size.height >= imgArea * 0.92) { bestC.delete(); return null; }
      pts = cv.RotatedRect.points(rect).map((p: any) => ({ x: p.x, y: p.y }));
    }
    bestC.delete();
    return pts;
  } catch {
    return null;
  } finally {
    for (const m of mats) { try { m.delete(); } catch { /* noop */ } }
  }
}

// 余白トリム (2026-07-24 v3.1): 切り取り後に残る「紙より暗い机の縁」を四辺から刈り込む。
// 判定は紙画素の割合 (中央値だと『半分紙・半分机』の行を余白と誤判定して本文を切る — 実測)。
function trimNonPaperMargins(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const w = canvas.width, h = canvas.height;
  const ctx = canvas.getContext('2d')!;
  const d = ctx.getImageData(0, 0, w, h).data;
  const lumAt = (x: number, y: number) => { const i = (y * w + x) * 4; return (d[i] * 299 + d[i + 1] * 587 + d[i + 2] * 114) / 1000; };
  const samp: number[] = [];
  for (let y = Math.floor(h * 0.3); y < h * 0.7; y += 4) for (let x = Math.floor(w * 0.3); x < w * 0.7; x += 4) samp.push(lumAt(x, y));
  samp.sort((a, b) => a - b);
  const paper = samp[Math.floor(samp.length * 0.6)] || 200;
  const thr = paper - 22;
  const colPaperFrac = (x: number, yy0: number, yy1: number) => { let n = 0, p = 0; for (let y = yy0; y < yy1; y += 3) { n++; if (lumAt(x, y) >= thr) p++; } return n ? p / n : 1; };
  const rowPaperFrac = (y: number, xx0: number, xx1: number) => { let n = 0, p = 0; for (let x = xx0; x < xx1; x += 3) { n++; if (lumAt(x, y) >= thr) p++; } return n ? p / n : 1; };
  let x0 = 0, x1 = w, y0 = 0, y1 = h;
  const capX = Math.floor(w * 0.4), capY = Math.floor(h * 0.4);
  for (let pass = 0; pass < 3; pass++) {
    while (x0 < capX && colPaperFrac(x0, y0, y1) < 0.12) x0++;
    while (w - x1 < capX && colPaperFrac(x1 - 1, y0, y1) < 0.12) x1--;
    while (y0 < capY && rowPaperFrac(y0, x0, x1) < 0.12) y0++;
    while (h - y1 < capY && rowPaperFrac(y1 - 1, x0, x1) < 0.12) y1--;
  }
  x0 = Math.max(0, x0 - 4); y0 = Math.max(0, y0 - 4);
  x1 = Math.min(w, x1 + 4); y1 = Math.min(h, y1 + 4);
  if (x1 - x0 < 200 || y1 - y0 < 200) return canvas;
  if (x0 === 0 && y0 === 0 && x1 === w && y1 === h) return canvas;
  const out = document.createElement('canvas');
  out.width = x1 - x0; out.height = y1 - y0;
  out.getContext('2d')!.drawImage(canvas, x0, y0, x1 - x0, y1 - y0, 0, 0, x1 - x0, y1 - y0);
  return out;
}

// レベル補正 (2026-07-24 v3): ヒストグラムから黒点/白点を取り、明るさ・コントラストの
// 大域調整のみ行う。v2 の背景推定除算は文字周辺に白い靄 (ハロー) を作り読みにくかった
// (user 指摘) ため、大域 LUT に置換。ゲイン上限つきで紙目の斑点増幅も抑える。
function applyLevels(canvas: HTMLCanvasElement, cropped: boolean) {
  const w = canvas.width, h = canvas.height;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const hist = new Uint32Array(256);
  for (let i = 0; i < d.length; i += 4) {
    hist[(d[i] * 299 + d[i + 1] * 587 + d[i + 2] * 114) / 1000 | 0]++;
  }
  const total = w * h;
  const pct = (p: number) => {
    let acc = 0;
    for (let v = 0; v < 256; v++) { acc += hist[v]; if (acc >= total * p) return v; }
    return 255;
  };
  // 白点 = 紙の輝度 (切り取り済みなら P90、未切り取りは背景混在なので控えめに P96)
  let wp = pct(cropped ? 0.90 : 0.96);
  let bp = pct(0.015);
  wp = Math.min(wp, 250);
  if (wp - bp < 60) bp = Math.max(0, wp - 60);
  let gain = 255 / Math.max(1, wp - bp);
  if (gain > 3) gain = 3; // 増幅上限 (紙目・ノイズの斑点化防止)
  const lut = new Uint8ClampedArray(256);
  for (let v = 0; v < 256; v++) lut[v] = Math.max(0, Math.min(255, (v - bp) * gain));
  for (let i = 0; i < d.length; i += 4) { d[i] = lut[d[i]]; d[i + 1] = lut[d[i + 1]]; d[i + 2] = lut[d[i + 2]]; }
  ctx.putImageData(img, 0, 0);
}

// 書類四辺形検出 + 射影変換。検出できなければ null (呼び出し側でフォールバック)。
function scanCropWithCv(cv: any, canvas: HTMLCanvasElement): HTMLCanvasElement | null {
  if (!cv || !cv.Mat) return null;
  const mats: any[] = [];
  const track = (m: any) => { mats.push(m); return m; };
  try {
    const src = track(cv.imread(canvas));
    const gray = track(new cv.Mat());
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    const blur = track(new cv.Mat());
    cv.GaussianBlur(gray, blur, new cv.Size(5, 5), 0);
    const edges = track(new cv.Mat());
    cv.Canny(blur, edges, 60, 180);
    const kernel = track(cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(5, 5)));
    cv.dilate(edges, edges, kernel);
    const contours = new cv.MatVector();
    const hierarchy = track(new cv.Mat());
    cv.findContours(edges, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);
    const imgArea = canvas.width * canvas.height;
    let best: { pts: Array<{ x: number; y: number }>; area: number } | null = null;
    let largest: { contour: any; area: number } | null = null; // fallback 用 (jscanify 方式)
    for (let i = 0; i < contours.size(); i++) {
      const c = contours.get(i);
      const area = cv.contourArea(c);
      if (area < imgArea * 0.2) { c.delete(); continue; }
      if (!largest || area > largest.area) {
        if (largest) largest.contour.delete();
        largest = { contour: c, area };
      }
      const peri = cv.arcLength(c, true);
      const approx = new cv.Mat();
      cv.approxPolyDP(c, approx, 0.02 * peri, true);
      if (approx.rows === 4 && (!best || area > best.area)) {
        const pts: Array<{ x: number; y: number }> = [];
        for (let r = 0; r < 4; r++) pts.push({ x: approx.data32S[r * 2], y: approx.data32S[r * 2 + 1] });
        best = { pts, area };
      }
      approx.delete();
      if (!largest || largest.contour !== c) c.delete();
    }
    contours.delete();
    // fallback: 4角形近似に失敗しても最大輪郭から jscanify 方式 (中心から4象限の最遠点) で角を推定
    if (!best && largest) {
      try {
        const rect = cv.minAreaRect(largest.contour);
        const cx = rect.center.x, cy = rect.center.y;
        const quad: Array<{ x: number; y: number; d: number } | null> = [null, null, null, null]; // TL,TR,BR,BL
        const n = largest.contour.data32S.length / 2;
        for (let i = 0; i < n; i++) {
          const x = largest.contour.data32S[i * 2], y = largest.contour.data32S[i * 2 + 1];
          const d = Math.hypot(x - cx, y - cy);
          const qi = x < cx ? (y < cy ? 0 : 3) : (y < cy ? 1 : 2);
          if (!quad[qi] || d > quad[qi]!.d) quad[qi] = { x, y, d };
        }
        if (quad.every(Boolean)) best = { pts: quad.map((q) => ({ x: q!.x, y: q!.y })), area: largest.area };
      } catch { /* fallback なし */ }
    }
    if (largest) largest.contour.delete();
    // 最終 fallback: 明部(紙)セグメンテーション — レシートの主経路 (2026-07-24)
    if (!best) {
      const pts = brightPaperCorners(cv, src, imgArea);
      if (pts) best = { pts, area: imgArea };
    }
    if (!best) return null;
    // 角の並べ替え: 左上=Σ最小 / 右下=Σ最大 / 右上=差(x−y)最大 / 左下=差最小
    const bySum = [...best.pts].sort((a, b) => (a.x + a.y) - (b.x + b.y));
    const byDiff = [...best.pts].sort((a, b) => (a.x - a.y) - (b.x - b.y));
    const tl = bySum[0], br = bySum[3], bl = byDiff[0], tr = byDiff[3];
    const dist = (p: any, q: any) => Math.hypot(p.x - q.x, p.y - q.y);
    const outW = Math.round(Math.max(dist(tl, tr), dist(bl, br)));
    const outH = Math.round(Math.max(dist(tl, bl), dist(tr, br)));
    if (outW < 200 || outH < 200) return null;
    const srcTri = track(cv.matFromArray(4, 1, cv.CV_32FC2, [tl.x, tl.y, tr.x, tr.y, br.x, br.y, bl.x, bl.y]));
    const dstTri = track(cv.matFromArray(4, 1, cv.CV_32FC2, [0, 0, outW, 0, outW, outH, 0, outH]));
    const M = track(cv.getPerspectiveTransform(srcTri, dstTri));
    const warped = track(new cv.Mat());
    cv.warpPerspective(src, warped, M, new cv.Size(outW, outH), cv.INTER_LINEAR, cv.BORDER_REPLICATE);
    const out = document.createElement('canvas');
    out.width = outW; out.height = outH;
    cv.imshow(out, warped);
    return out;
  } catch {
    return null;
  } finally {
    for (const m of mats) { try { m.delete(); } catch { /* noop */ } }
  }
}

export type EnhanceMode = 'color' | 'bw';

// image2scan 手順④相当: 適応的二値化 (skimage threshold_local(block=11, offset=10, gaussian) と
// 等価な cv.adaptiveThreshold GAUSSIAN_C 11/10) — 完全な白地スキャン風の白黒出力。
function adaptiveBinarizeWithCv(cv: any, canvas: HTMLCanvasElement): boolean {
  if (!cv || !cv.Mat) return false;
  const mats: any[] = [];
  const track = (m: any) => { mats.push(m); return m; };
  try {
    const src = track(cv.imread(canvas));
    const gray = track(new cv.Mat());
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    const bin = track(new cv.Mat());
    cv.adaptiveThreshold(gray, bin, 255, cv.ADAPTIVE_THRESH_GAUSSIAN_C, cv.THRESH_BINARY, 11, 10);
    cv.imshow(canvas, bin);
    return true;
  } catch {
    return false;
  } finally {
    for (const m of mats) { try { m.delete(); } catch { /* noop */ } }
  }
}

async function enhanceDocumentImage(file: File | Blob, mode: EnhanceMode = 'color'): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  const maxSide = 1800;
  const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale); const h = Math.round(bmp.height * scale);
  let canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d')!.drawImage(bmp, 0, 0, w, h);
  let cvRef: any = null;
  let didCrop = false;
  {
    // ① スキャン切り取り (書類検出できたときのみ)
    try {
      cvRef = await Promise.race([loadCv(), new Promise((r) => setTimeout(() => r(null), 9000))]);
      const cropped = cvRef ? scanCropWithCv(cvRef, canvas) : null;
      if (cropped) { canvas = cropped; didCrop = true; }
    } catch { /* フォールバック: 切り取りなし */ }
  }
  // ②' 白黒モード: image2scan 本物と同一の適応的二値化 (cv 不在時はカラー補正へフォールバック)
  if (mode === 'bw' && cvRef && adaptiveBinarizeWithCv(cvRef, canvas)) {
    return new Promise((resolve) => canvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.85));
  }
  // ② 余白トリム (紙より暗い机の縁を四辺から除去) → ③ レベル補正 (大域調整のみ — 靄なし)
  canvas = trimNonPaperMargins(canvas);
  applyLevels(canvas, didCrop);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.85));
}

// ---------------------------------------------------------------------------
// PDF 取込 (2026-10-08): 紙をスキャンした PDF を D&D / ファイル選択で提出できるようにする。
//   提出後の経路 (miniPC AI分析 / DocAligner / 一覧表示 / freee 証憑添付) は全て画像前提なので、
//   ブラウザ内で 1ページ=1枚の JPEG に変換して既存の写真スロットへ流す (サーバー変更なし)。
//   pdf.js は PdfViewer (@react-pdf-viewer) と同じ pdfjs-dist を使い、worker も同じ CDN から読む。
// ---------------------------------------------------------------------------
const PDF_PAGE_LONG_SIDE = 1800; // 写真の補正出力 (enhanceDocumentImage の maxSide) と揃える
let pdfjsPromise: Promise<any> | null = null;
function loadPdfjs(): Promise<any> {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then((m: any) => {
      const lib = m.getDocument ? m : m.default;
      lib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${lib.version}/build/pdf.worker.min.js`;
      return lib;
    }).catch((e) => { pdfjsPromise = null; throw e; });
  }
  return pdfjsPromise;
}

// 先頭 maxPages ページだけ描画する (total は PDF の総ページ数 — 溢れ分の案内用)
async function pdfToPageImages(file: File, maxPages: number): Promise<{ pages: File[]; total: number }> {
  const pdfjs = await loadPdfjs();
  const doc = await pdfjs.getDocument({
    data: new Uint8Array(await file.arrayBuffer()),
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
  }).promise;
  try {
    const total: number = doc.numPages;
    const stem = file.name.replace(/\.pdf$/i, '') || 'scan';
    const pages: File[] = [];
    for (let p = 1; p <= Math.min(total, maxPages); p++) {
      const page = await doc.getPage(p);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: PDF_PAGE_LONG_SIDE / Math.max(base.width, base.height) });
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(viewport.width); canvas.height = Math.round(viewport.height);
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport }).promise;
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      if (!blob) throw new Error('pdf_page_encode_failed');
      pages.push(new File([blob], `${stem}_p${p}.jpg`, { type: 'image/jpeg' }));
    }
    return { pages, total };
  } finally {
    try { doc.destroy(); } catch { /* noop */ }
  }
}

const isPdfFile = (f: File) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name);

// ---------------------------------------------------------------------------
// 写真スロット (プレビュー + 補正モード: スキャン(カラー) → スキャン(白黒) → OFF)
// ---------------------------------------------------------------------------
type SlotMode = EnhanceMode | 'off';
interface PhotoSlot { original: File; mode: SlotMode; cache: Partial<Record<EnhanceMode, Blob>>; previewUrl: string }

const SLOT_MODE_LABEL: Record<SlotMode, string> = { color: 'スキャン(カラー)', bw: 'スキャン(白黒)', off: '補正OFF' };
const SLOT_MODE_NEXT: Record<SlotMode, SlotMode> = { color: 'bw', bw: 'off', off: 'color' };

function slotCurrentBlob(s: PhotoSlot): Blob {
  return s.mode === 'off' ? s.original : (s.cache[s.mode] || s.original);
}

// 画像の拡大表示 (提出フォームのプレビュー / 出納帳の写真 共用)。背景クリック or Esc で閉じる
const ImageLightbox: React.FC<{ src: string | null; title?: string; onClose: () => void }> = ({ src, title, onClose }) => {
  useEffect(() => {
    if (!src) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [src, onClose]);
  if (!src) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,14,12,0.75)', zIndex: 1300, cursor: 'zoom-out' }} />
      <div style={{ position: 'fixed', top: '3vh', left: '50%', transform: 'translateX(-50%)', zIndex: 1301, maxWidth: '94vw', maxHeight: '92vh', overflow: 'auto', background: '#26231f', borderRadius: 10, padding: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
          <div style={{ color: '#e8e4de', fontSize: '0.8125rem', fontWeight: 700, paddingLeft: 4 }}>{title || ''}</div>
          <Button appearance="tertiary" small onClick={onClose} IconComponent={MdClose}>閉じる</Button>
        </div>
        <img src={src} alt={title || '拡大'} style={{ display: 'block', maxWidth: '90vw', maxHeight: '82vh' }} />
      </div>
    </>
  );
};

const PhotoInput: React.FC<{
  label: string; slot: PhotoSlot | null;
  onPick: (files: File[]) => void; onToggleEnhance: () => void; onClear: () => void; onZoom: () => void;
}> = ({ label, slot, onPick, onToggleEnhance, onClear, onZoom }) => {
  const ref = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault(); e.stopPropagation(); setOver(false);
        const fs = Array.from(e.dataTransfer.files || []);
        if (fs.length) onPick(fs);
      }}
      style={{ border: `1px dashed ${over ? ACCENT : (slot ? RULE : '#c9c4bc')}`, borderRadius: 10, padding: 10, width: 168, background: over ? ACCENT_BG : (slot ? '#fff' : '#faf9f7') }}>
      <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: SUB, marginBottom: 6 }}>{label}</div>
      {slot ? (
        <>
          <img src={slot.previewUrl} alt={label} title="クリックで拡大" onClick={onZoom}
            style={{ width: '100%', height: 110, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in' }} />
          <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
            <button type="button" onClick={onToggleEnhance} title="タップで スキャン(カラー)→スキャン(白黒)→補正OFF を切替" style={{
              cursor: 'pointer', fontSize: '0.625rem', fontWeight: 700, padding: '3px 7px', borderRadius: 6,
              border: `1px solid ${slot.mode !== 'off' ? GOOD : '#d5d0c8'}`, background: slot.mode !== 'off' ? GOOD_BG : '#fff',
              color: slot.mode !== 'off' ? GOOD : SUB,
            }}>{SLOT_MODE_LABEL[slot.mode]}</button>
            <button type="button" onClick={onClear} style={{ cursor: 'pointer', border: 'none', background: 'transparent', color: NEG, fontWeight: 800 }}>×</button>
          </div>
        </>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} style={{
          cursor: 'pointer', width: '100%', height: 110, borderRadius: 6, border: 'none', background: '#f1efec',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: SUB, fontWeight: 700, fontSize: '0.75rem',
        }}><MdPhotoCamera size={26} /> 撮影 / 選択</button>
      )}
      {!slot && (
        <button type="button" onClick={() => fileRef.current?.click()} title="PDF・画像ファイルを選択 (この枠へのドラッグ&ドロップも可)" style={{
          cursor: 'pointer', width: '100%', marginTop: 6, padding: '4px 0', borderRadius: 6, border: `1px solid ${RULE}`, background: '#fff',
          color: SUB, fontWeight: 700, fontSize: '0.625rem',
        }}>PDF を選択 / ドロップ</button>
      )}
      <input ref={ref} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick([f]); e.target.value = ''; }} />
      {/* capture なし = カメラ固定にならない (PC のファイル選択 / iPhone の「ファイル」から PDF を選べる) */}
      <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple style={{ display: 'none' }}
        onChange={(e) => { const fs = Array.from(e.target.files || []); if (fs.length) onPick(fs); e.target.value = ''; }} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// キオスク判定 (iOS ホーム画面アイコン用): #petty_cash/submit?s=<section_id>
// ---------------------------------------------------------------------------
function kioskSectionFromHash(): { section: number; book: PettyCashBook; chief: string | null } | null {
  if (typeof window === 'undefined') return null;
  if (!/^#?petty_cash\/submit(\?|$)/i.test(window.location.hash)) return null;
  const h = window.location.hash;
  const m = h.match(/[?&]s=(\d+)/);
  const saved = Number(localStorage.getItem('pc_kiosk_section') || 0);
  const section = m ? Number(m[1]) : (saved > 0 ? saved : 3068544);
  const book: PettyCashBook = /[?&]b=(ca|chief_advance)(&|$)/.test(h) ? 'chief_advance' : 'petty_cash';
  const cm = h.match(/[?&]c=([^&]+)/);
  const chief = cm ? decodeURIComponent(cm[1]) : null;
  return { section, book, chief };
}

// ---------------------------------------------------------------------------
// 提出フォーム (タブ / キオスク 共用)
// ---------------------------------------------------------------------------
const SubmitForm: React.FC<{
  sections: Map<number, string>;
  storeOptions: Array<{ name: string; value: string }>;
  section: number;
  onSectionChange: (id: number) => void;
  onSubmitted: (entryId: number, weekKey: string) => void;
  flash: (m: string) => void;
  book?: PettyCashBook;
  initialChief?: string | null;
}> = ({ storeOptions, section, onSectionChange, onSubmitted, flash, book = 'petty_cash', initialChief = null }) => {
  const [slip, setSlip] = useState<PhotoSlot | null>(null);
  const [receiptSlots, setReceiptSlots] = useState<Array<PhotoSlot | null>>([null, null, null, null]);
  const [note, setNote] = useState('');
  const [chief, setChief] = useState<string>(initialChief && (PETTY_CASH_CHIEFS as readonly string[]).includes(initialChief) ? initialChief : PETTY_CASH_CHIEFS[0]);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [zoom, setZoom] = useState<{ src: string; title: string } | null>(null);
  const closeZoom = useCallback(() => setZoom(null), []);
  const zoomSlot = (s: PhotoSlot | null, label: string) => { if (s) setZoom({ src: s.previewUrl, title: `${label} — ${SLOT_MODE_LABEL[s.mode]}` }); };

  // OpenCV (スキャン切り取り) を撮影前に温めておく (13MB 遅延チャンク)
  useEffect(() => { loadCv(); }, []);

  // enhance=false: PDF 由来のページ。既にスキャン済みなので切り取り/補正は初期 OFF (誤切り取り防止・手動で切替は可)
  const makeSlot = async (f: File, enhance = true): Promise<PhotoSlot> => {
    const color = enhance ? await enhanceDocumentImage(f, 'color').catch(() => null) : null;
    const slot: PhotoSlot = { original: f, mode: color ? 'color' : 'off', cache: color ? { color } : {}, previewUrl: '' };
    slot.previewUrl = URL.createObjectURL(slotCurrentBlob(slot));
    return slot;
  };

  // ファイル取込 (撮影 / ファイル選択 / D&D 共通)。スロット通し番号 k: 0=出金伝票, 1..4=証憑1..4。
  //   startK 以降の空きスロットへ順に 1枚ずつ。PDF は 1ページ=1枚。入り切らない分は取り込まず案内する
  //   (R8: 1提出あたり伝票込み5枚まで)。
  const importFiles = async (startK: number, files: File[]) => {
    const filled = [!!slip, ...receiptSlots.map(Boolean)];
    const targets: number[] = [];
    for (let k = startK; k < filled.length; k++) if (!filled[k]) targets.push(k);
    if (targets.length === 0) { flash('空きスロットがありません (1提出あたり伝票込み5枚まで)'); return; }
    const made: PhotoSlot[] = [];
    const failed: string[] = [];
    let overflow = 0;
    setImporting(true);
    try {
      for (const f of files) {
        const room = targets.length - made.length;
        if (isPdfFile(f)) {
          try {
            const { pages, total } = await pdfToPageImages(f, room);
            for (const p of pages) made.push(await makeSlot(p, false));
            overflow += total - pages.length;
          } catch { failed.push(f.name); }
        } else if (f.type.startsWith('image/')) {
          if (room <= 0) { overflow += 1; continue; }
          // ブラウザで表示できない画像 (HEIC 等) は壊れたプレビューのまま提出させず、ここで弾く
          const decodable = await createImageBitmap(f).then((b) => { b.close(); return true; }).catch(() => false);
          if (decodable) made.push(await makeSlot(f)); else failed.push(f.name);
        } else failed.push(f.name);
      }
    } finally { setImporting(false); }
    const byK = new Map<number, PhotoSlot>();
    made.forEach((s, i) => byK.set(targets[i], s));
    if (byK.has(0)) setSlip(byK.get(0)!);
    setReceiptSlots((arr) => arr.map((x, j) => byK.get(j + 1) || x));
    const msgs: string[] = [];
    if (overflow > 0) msgs.push(`空きスロットが足りず ${overflow} 枚(ページ)は取り込めませんでした (1提出あたり伝票込み5枚まで)`);
    if (failed.length > 0) msgs.push(`読み込めませんでした: ${failed.join(', ')} (PDF か JPEG/PNG 画像のみ)`);
    if (msgs.length > 0) flash(msgs.join(' / '));
  };
  // 補正モード循環: カラー→白黒→OFF→カラー。白黒は初回選択時に生成してキャッシュ
  const toggleEnhance = async (s: PhotoSlot): Promise<PhotoSlot> => {
    const mode = SLOT_MODE_NEXT[s.mode];
    const cache = { ...s.cache };
    if (mode !== 'off' && !cache[mode]) {
      const b = await enhanceDocumentImage(s.original, mode).catch(() => null);
      if (b) cache[mode] = b;
    }
    URL.revokeObjectURL(s.previewUrl);
    const next: PhotoSlot = { ...s, mode: mode !== 'off' && !cache[mode] ? 'off' : mode, cache, previewUrl: '' };
    next.previewUrl = URL.createObjectURL(slotCurrentBlob(next));
    return next;
  };

  const doSubmit = async () => {
    const receipts = receiptSlots.filter((s): s is PhotoSlot => !!s);
    if (!slip && receipts.length === 0) { flash('出金伝票または証憑を撮影するか、PDF を追加してください'); return; }
    setSubmitting(true);
    try {
      const r = await submitPettyCashEntry({
        section_id: section,
        book,
        chief_name: book === 'chief_advance' ? chief : undefined,
        note: note.trim() || undefined,
        slip: slip ? slotCurrentBlob(slip) : null,
        receipts: receipts.map((s) => slotCurrentBlob(s)),
      });
      flash(`提出しました (#${r.id}・支払週 ${r.week_key})。AI が仕訳を分析します`);
      setSlip(null); setReceiptSlots([null, null, null, null]); setNote('');
      onSubmitted(r.id, r.week_key);
    } catch (e: any) { flash(`提出失敗: ${e?.message || ''}`); }
    finally { setSubmitting(false); }
  };

  return (
    <div>
      <div style={{ marginBottom: 14, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '0.625rem', color: SUB, fontWeight: 700, marginBottom: 4 }}>店舗</div>
          <SelectBox name="pcsubsec" value={String(section)} options={storeOptions} width="medium" onChange={(e: any) => onSectionChange(Number(e.target.value))} />
        </div>
        {book === 'chief_advance' && (
          <div>
            <div style={{ fontSize: '0.625rem', color: SUB, fontWeight: 700, marginBottom: 4 }}>立替した室長</div>
            <SelectBox name="pcchief" value={chief} options={PETTY_CASH_CHIEFS.map((c) => ({ name: c, value: c }))} width="small" onChange={(e: any) => setChief(e.target.value)} />
          </div>
        )}
      </div>
      {/* 枠の外(スロットの隙間)へ落としてもブラウザが PDF を開いて入力が消えないよう、行全体で受ける */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: importing ? 6 : 14 }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); const fs = Array.from(e.dataTransfer.files || []); if (fs.length) importFiles(0, fs); }}>
        <PhotoInput label="出金伝票" slot={slip}
          onPick={(fs) => importFiles(0, fs)}
          onZoom={() => zoomSlot(slip, '出金伝票')}
          onToggleEnhance={async () => { if (slip) setSlip(await toggleEnhance(slip)); }}
          onClear={() => setSlip(null)} />
        {receiptSlots.map((s, i) => (
          (i === 0 || receiptSlots[i - 1]) ? (
            <PhotoInput key={i} label={`証憑 ${i + 1}`} slot={s}
              onPick={(fs) => importFiles(i + 1, fs)}
              onZoom={() => zoomSlot(receiptSlots[i], `証憑 ${i + 1}`)}
              onToggleEnhance={async () => { const cur = receiptSlots[i]; if (cur) { const nx = await toggleEnhance(cur); setReceiptSlots((arr) => arr.map((x, j) => (j === i ? nx : x))); } }}
              onClear={() => setReceiptSlots((arr) => arr.map((x, j) => (j === i ? null : x)))} />
          ) : null
        ))}
      </div>
      {importing && (
        <div style={{ marginBottom: 14, fontSize: '0.75rem', color: SUB, fontWeight: 700 }}><InlineSpinner isLoading /> 読み込み中…</div>
      )}
      <ImageLightbox src={zoom?.src || null} title={zoom?.title} onClose={closeZoom} />
      <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="メモ (任意 — 内容の補足があれば)"
        style={{ width: '100%', maxWidth: 560, minHeight: 54, border: `1px solid ${RULE}`, borderRadius: 8, padding: '8px 12px', fontSize: '0.8125rem', boxSizing: 'border-box' }} />
      <div style={{ marginTop: 14 }}>
        <Button appearance="primary" onClick={doSubmit} disabled={submitting}>{submitting ? <InlineSpinner isLoading /> : null} 提出して AI 分析へ</Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// iPhone 配布カード: 店舗ごとの提出用 URL + QR (ホーム画面追加用)
// ---------------------------------------------------------------------------
const StoreLinkCard: React.FC<{ storeOptions: Array<{ name: string; value: string }>; flash: (m: string) => void; book?: PettyCashBook }> = ({ storeOptions, flash, book = 'petty_cash' }) => {
  const [open, setOpen] = useState(false);
  const [qrs, setQrs] = useState<Record<string, string>>({});
  const base = `${window.location.origin}${window.location.pathname}`;
  const urlFor = (id: string) => `${base}#petty_cash/submit?s=${id}${book === 'chief_advance' ? '&b=ca' : ''}`;
  const appName = BOOK_META[book].kioskTitle;

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      const next: Record<string, string> = {};
      for (const o of storeOptions) {
        try { next[o.value] = await QRCode.toDataURL(urlFor(o.value), { width: 180, margin: 1 }); } catch {}
      }
      if (alive) setQrs(next);
    })();
    return () => { alive = false; };
  }, [open, storeOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ marginTop: 20, borderTop: `1px dashed ${RULE}`, paddingTop: 14 }}>
      <button type="button" onClick={() => setOpen(!open)} style={{ cursor: 'pointer', border: 'none', background: 'none', color: ACCENT, fontWeight: 800, fontSize: '0.8125rem', padding: 0 }}>
        📱 店舗の iPhone にこの機能を追加する {open ? '▲' : '▼'}
      </button>
      {open && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: '0.75rem', color: SUB, marginBottom: 12, lineHeight: 1.7 }}>
            各店舗の iPhone で下の QR を読み取り (または URL を開き)、Safari の共有ボタン →「<b>ホーム画面に追加</b>」。
            カメラアイコンの「{appName}」アプリとしてホーム画面に並び、タップでそのまま撮影→提出できます (店舗は自動選択済み)。
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
            {storeOptions.map((o) => (
              <div key={o.value} style={{ border: `1px solid ${LINE}`, borderRadius: 10, padding: 10, width: 170, textAlign: 'center', background: '#fff' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 800, color: INK, marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.name}</div>
                {qrs[o.value]
                  ? <img src={qrs[o.value]} alt={`QR ${o.name}`} style={{ width: 140, height: 140 }} />
                  : <div style={{ width: 140, height: 140, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: FAINT }}><InlineSpinner isLoading /></div>}
                <button type="button" onClick={() => { navigator.clipboard?.writeText(urlFor(o.value)); flash(`${o.name} の提出URLをコピーしました`); }}
                  style={{ marginTop: 6, cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 700, border: `1px solid ${RULE}`, borderRadius: 6, background: '#fff', color: SUB, padding: '3px 8px', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                  <MdContentCopy size={11} /> URL コピー
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// キオスクビュー (iOS ホーム画面アイコンから起動する提出専用画面)
// ---------------------------------------------------------------------------
const KioskView: React.FC<{ initialSection: number; book: PettyCashBook; initialChief: string | null }> = ({ initialSection, book, initialChief }) => {
  const [sections, setSections] = useState<Map<number, string>>(new Map());
  const [section, setSection] = useState<number>(initialSection);
  const [recent, setRecent] = useState<PettyCashEntry[] | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flash = useCallback((m: string) => {
    setToast(m);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => { fetchSectionsMap().then(setSections).catch(() => {}); }, []);
  useEffect(() => {
    // ホーム画面追加時のアイコン/タイトルを提出専用に (Safari は追加時点の DOM を読む)
    document.title = BOOK_META[book].kioskTitle;
    const link = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement | null;
    if (link) link.href = `${window.location.origin}${window.location.pathname.replace(/[^/]*$/, '')}pc-camera-icon.png`.replace(/\/\/pc-camera/, '/pc-camera');
  }, [book]);
  useEffect(() => { localStorage.setItem('pc_kiosk_section', String(section)); }, [section]);

  const loadRecent = useCallback(() => {
    fetchPettyCashLedger(section, book).then((r) => setRecent(r.entries.slice(0, 6))).catch(() => setRecent([]));
  }, [section, book]);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  const storeOptions = useMemo(() => {
    const all = Array.from(sections.entries());
    const ordered = [
      ...STORE_FIRST_ORDER.filter((id) => sections.has(id)).map((id) => [id, sections.get(id)!] as [number, string]),
      ...all.filter(([id]) => !STORE_FIRST_ORDER.includes(id)),
    ];
    const opts = ordered.map(([id, name]) => ({ name, value: String(id) }));
    return opts.length ? opts : [{ name: `部門#${section}`, value: String(section) }];
  }, [sections, section]);

  return (
    <div style={{ background: '#f8f7f5', minHeight: '100vh', paddingBottom: '3rem' }}>
      <div style={{ background: '#26231f', color: '#fff', padding: '14px 16px calc(14px + env(safe-area-inset-top) * 0)', display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span style={{ fontWeight: 900, fontSize: '1rem' }}>{BOOK_META[book].kioskTitle}</span>
        <span style={{ fontSize: '0.6875rem', opacity: 0.7 }}>{sections.get(section) || ''}</span>
      </div>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '14px 12px' }}>
        <CardBase>
          <div style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: '0.75rem', color: SUB, marginBottom: 12 }}>
              出金伝票と証憑を撮影して提出してください ({book === 'chief_advance' ? '金曜午前締め → 当日 本部現金から出金' : '金曜午前締め → 当日出金'})。撮影後は自動で書類向けに補正されます。
            </div>
            <SubmitForm sections={sections} storeOptions={storeOptions} section={section} onSectionChange={setSection}
              onSubmitted={() => loadRecent()} flash={flash} book={book} initialChief={initialChief} />
          </div>
        </CardBase>
        <CardBase>
          <div style={{ padding: '12px 16px' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 800, color: INK, marginBottom: 8 }}>この店舗の最近の提出</div>
            {!recent && <div style={{ color: SUB, padding: 8 }}><InlineSpinner isLoading /></div>}
            {recent && recent.length === 0 && <div style={{ color: FAINT, fontSize: '0.75rem', padding: 8 }}>まだ提出はありません</div>}
            {(recent || []).map((e) => {
              const m = STATUS_META[e.status] || STATUS_META.submitted;
              return (
                <div key={e.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 0', borderBottom: `1px dashed ${LINE}`, fontSize: '0.75rem' }}>
                  <span style={{ ...numCss, color: SUB, width: 72 }}>{e.occurred_on || e.submitted_at.slice(5, 10)}</span>
                  <span style={{ flex: 1, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.partner_name || '(分析待ち)'}
                    {e.chief_name && <span style={{ marginLeft: 6, fontWeight: 400, color: SUB, fontSize: '0.6875rem' }}>({e.chief_name})</span>}
                  </span>
                  <span style={{ ...numCss, fontWeight: 800 }}>{yen(e.amount)}</span>
                  <span style={{ fontSize: '0.625rem', fontWeight: 800, padding: '2px 7px', borderRadius: 999, color: m.color, background: m.bg, whiteSpace: 'nowrap' }}>{m.label}</span>
                </div>
              );
            })}
          </div>
        </CardBase>
      </div>
      {toast && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, minWidth: 260, maxWidth: '92vw' }}>
          <MessageBlock success>{toast}</MessageBlock>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// 手動修正パネル (2026-07-24): freee マスタをリアルタイム取得し、AI を介さず
// 発生日/取引先/勘定科目/税区分/品目/メモタグ/部門/金額/摘要を直接編集する。
// ---------------------------------------------------------------------------
interface EditRow { account_item_id: number; tax_code: number; item_id: number | null; tag_ids: number[]; section_id: number; amount: number; description: string }

const selSt: React.CSSProperties = { border: `1px solid ${RULE}`, borderRadius: 6, padding: '4px 6px', fontSize: '0.75rem', maxWidth: 170, background: '#fff' };

const ManualEditPanel: React.FC<{
  entry: PettyCashEntry;
  sections: Map<number, string>;
  onSaved: () => void;
  onClose: () => void;
  flash: (m: string) => void;
}> = ({ entry, sections, onSaved, onClose, flash }) => {
  const [accounts, setAccounts] = useState<Map<number, string>>(new Map());
  const [taxes, setTaxes] = useState<Map<number, string>>(new Map());
  const [items, setItems] = useState<Map<number, string>>(new Map());
  const [tags, setTags] = useState<Map<number, string>>(new Map());
  const [partners, setPartners] = useState<FreeePartnerLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const pd = entry.proposed_deal;
  const [occurredOn, setOccurredOn] = useState<string>(entry.occurred_on || (pd?.issue_date ?? '') || '');
  const [partnerQuery, setPartnerQuery] = useState<string>(entry.partner_name || '');
  const [partnerId, setPartnerId] = useState<number | null>(entry.partner_id);
  const [rows, setRows] = useState<EditRow[]>(() => (Array.isArray(pd?.details) && pd.details.length ? pd.details : [{}]).map((d: any) => ({
    account_item_id: Number(d.account_item_id) || 0,
    tax_code: d.tax_code != null ? Number(d.tax_code) : 136,
    item_id: d.item_id != null ? Number(d.item_id) : null,
    tag_ids: Array.isArray(d.tag_ids) ? d.tag_ids.map(Number) : [],
    section_id: Number(d.section_id) || entry.section_id,
    amount: Number(d.amount) || 0,
    description: String(d.description || ''),
  })));

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // freee マスタをリアルタイム取得 (キャッシュ済 helper — 勘定/税/品目/タグ/取引先)
        const [ac, tx, it, tg, ps] = await Promise.all([
          fetchAccountItemsMap(), fetchTaxCodesMap(), fetchItemsMap(), fetchTagsMap(), fetchAllFreeePartners(),
        ]);
        if (!alive) return;
        setAccounts(ac); setTaxes(tx); setItems(it); setTags(tg); setPartners(ps);
      } catch (e: any) { flash(`マスタ取得失敗: ${e?.message || ''}`); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const partnerMatches = useMemo(() => {
    const q = partnerQuery.trim().toLowerCase();
    if (!q) return partners.slice(0, 20);
    return partners.filter((p) => `${p.name} ${p.long_name || ''} ${p.shortcut1 || ''}`.toLowerCase().includes(q)).slice(0, 20);
  }, [partners, partnerQuery]);
  const selectedPartner = partners.find((p) => p.id === partnerId) || null;

  const setRow = (i: number, patch: Partial<EditRow>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const save = async () => {
    if (saving) return;
    if (!occurredOn) { flash('発生日を入力してください'); return; }
    for (let i = 0; i < rows.length; i++) { const r = rows[i];
      if (!r.account_item_id) { flash(`明細${i + 1}: 勘定科目を選択してください`); return; }
      if (!Number.isInteger(r.amount) || r.amount === 0) { flash(`明細${i + 1}: 金額(整数)を入力してください`); return; }
    }
    setSaving(true);
    try {
      const details = rows.map((r) => ({
        account_item_id: r.account_item_id,
        tax_code: r.tax_code,
        item_id: r.item_id,
        tag_ids: r.tag_ids,
        section_id: r.section_id,
        amount: r.amount,
        description: r.description.slice(0, 240),
        accountName: accounts.get(r.account_item_id) || null,
        taxCodeName: taxes.get(r.tax_code) || null,
        itemName: r.item_id ? items.get(r.item_id) || null : null,
        sectionName: sections.get(r.section_id) || null,
      }));
      const res = await manualEditPettyCashEntry(entry.id, {
        occurred_on: occurredOn,
        partner_id: partnerId,
        partner_name: selectedPartner ? selectedPartner.name : (partnerQuery.trim() || null),
        proposed_deal: { details },
        updated_by: 'web_app_user',
      });
      if ((res as any).error) { flash(`保存失敗: ${(res as any).error} ${((res as any).errors || []).join(' / ')}`); return; }
      flash('手動修正を保存しました');
      onSaved(); onClose();
    } catch (e: any) { flash(`保存失敗: ${e?.message || ''}`); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 14, color: SUB }}><InlineSpinner isLoading /> freee マスタ取得中…</div>;

  return (
    <div style={{ border: `1px solid ${ACCENT}`, borderRadius: 10, padding: '12px 14px', margin: '10px 0', background: '#fbfcff' }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 800, color: ACCENT, marginBottom: 10 }}>手動修正 (freee マスタから選択・AI を介しません)</div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
        <label style={{ fontSize: '0.6875rem', color: SUB, fontWeight: 700 }}>発生日<br />
          <input type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} style={{ ...selSt, marginTop: 3 }} />
        </label>
        <div style={{ fontSize: '0.6875rem', color: SUB, fontWeight: 700, position: 'relative' }}>取引先 (freee 登録名)<br />
          <input value={partnerQuery} placeholder="検索…"
            onChange={(e) => { setPartnerQuery(e.target.value); setPartnerId(null); }}
            style={{ ...selSt, marginTop: 3, width: 240, maxWidth: 240, borderColor: partnerId ? GOOD : RULE }} />
          {partnerId && <span style={{ position: 'absolute', right: 6, bottom: 7, color: GOOD, fontWeight: 900 }}>✓</span>}
          {!partnerId && partnerQuery.trim() && (
            <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, width: 300, maxHeight: 200, overflowY: 'auto', background: '#fff', border: `1px solid ${RULE}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
              {partnerMatches.map((p) => (
                <div key={p.id} onClick={() => { setPartnerId(p.id); setPartnerQuery(p.name); }}
                  style={{ padding: '6px 10px', fontSize: '0.75rem', cursor: 'pointer', borderBottom: `1px solid ${LINE}` }}>
                  {p.name}{p.long_name && p.long_name !== p.name ? <span style={{ color: FAINT }}> ({p.long_name})</span> : null}
                </div>
              ))}
              {partnerMatches.length === 0 && <div style={{ padding: '6px 10px', fontSize: '0.6875rem', color: FAINT }}>該当なし (取引先の新規作成はできません)</div>}
            </div>
          )}
        </div>
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ border: `1px dashed ${RULE}`, borderRadius: 8, padding: '8px 10px', marginBottom: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ fontSize: '0.625rem', color: SUB, fontWeight: 700 }}>勘定科目<br />
            <select value={r.account_item_id || ''} onChange={(e) => setRow(i, { account_item_id: Number(e.target.value) })} style={selSt}>
              <option value="">選択…</option>
              {Array.from(accounts.entries()).sort((a, b) => a[1].localeCompare(b[1], 'ja')).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '0.625rem', color: SUB, fontWeight: 700 }}>税区分<br />
            <select value={r.tax_code} onChange={(e) => setRow(i, { tax_code: Number(e.target.value) })} style={selSt}>
              {Array.from(taxes.entries()).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '0.625rem', color: SUB, fontWeight: 700 }}>品目<br />
            <select value={r.item_id || ''} onChange={(e) => setRow(i, { item_id: e.target.value ? Number(e.target.value) : null })} style={selSt}>
              <option value="">(なし)</option>
              {Array.from(items.entries()).sort((a, b) => a[1].localeCompare(b[1], 'ja')).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '0.625rem', color: SUB, fontWeight: 700 }}>メモタグ (複数可)<br />
            <select multiple size={3} value={r.tag_ids.map(String)}
              onChange={(e) => setRow(i, { tag_ids: Array.from(e.target.selectedOptions).map((o) => Number(o.value)) })}
              style={{ ...selSt, minWidth: 130 }}>
              {Array.from(tags.entries()).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '0.625rem', color: SUB, fontWeight: 700 }}>部門<br />
            <select value={r.section_id} onChange={(e) => setRow(i, { section_id: Number(e.target.value) })} style={selSt}>
              {Array.from(sections.entries()).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '0.625rem', color: SUB, fontWeight: 700 }}>金額(税込)<br />
            <input type="number" value={r.amount || ''} onChange={(e) => setRow(i, { amount: Number(e.target.value) })} style={{ ...selSt, width: 90 }} />
          </label>
          <label style={{ fontSize: '0.625rem', color: SUB, fontWeight: 700, flex: 1, minWidth: 140 }}>摘要<br />
            <input value={r.description} onChange={(e) => setRow(i, { description: e.target.value })} style={{ ...selSt, width: '100%', maxWidth: 'none' }} />
          </label>
          {rows.length > 1 && (
            <button type="button" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
              style={{ cursor: 'pointer', border: 'none', background: 'none', color: NEG, fontWeight: 900, fontSize: '1rem' }}>×</button>
          )}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={() => setRows((rs) => [...rs, { account_item_id: 0, tax_code: 136, item_id: null, tag_ids: [], section_id: entry.section_id, amount: 0, description: '' }])}
          style={{ cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 700, border: `1px solid ${RULE}`, borderRadius: 6, background: '#fff', color: SUB, padding: '4px 10px' }}>+ 行を追加</button>
        <span style={{ fontSize: '0.75rem', color: SUB }}>合計 <b style={{ ...numCss, color: INK }}>{yen(total)}</b></span>
        <div style={{ flex: 1 }} />
        <Button appearance="primary" small onClick={save} disabled={saving}>{saving ? <InlineSpinner isLoading /> : null} 保存</Button>
        <Button appearance="tertiary" small onClick={onClose} disabled={saving}>閉じる</Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// エントリ詳細 (写真・提案・コメント・操作)
// ---------------------------------------------------------------------------
const EntryDetail: React.FC<{
  entry: PettyCashEntry; sections: Map<number, string>;
  accounts: Map<number, string>; taxes: Map<number, string>;
  onChanged: () => void; flash: (m: string) => void;
}> = ({ entry, sections, accounts, taxes, onChanged, flash }) => {
  const [comments, setComments] = useState<PettyCashComment[] | null>(null);
  const [text, setText] = useState('');
  const [kind, setKind] = useState<'comment' | 'instruction'>('comment');
  const [learn, setLearn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [imgView, setImgView] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    fetchPettyCashEntry(entry.id).then((r) => setComments(r.comments)).catch(() => setComments([]));
  }, [entry.id, entry.updated_at]);

  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await postPettyCashComment(entry.id, { text: text.trim(), kind, learn: kind === 'instruction' ? learn : false });
      setText('');
      flash(kind === 'instruction' ? '修正指示を送信しました (AI が再分析します)' : 'コメントを追加しました');
      onChanged();
    } catch (e: any) { flash(`送信失敗: ${e?.message || ''}`); }
    finally { setBusy(false); }
  };

  const doRegister = async () => {
    if (busy) return;
    const bookDesc = entry.book === 'chief_advance'
      ? `口座: 現金(本部) / メモタグ: 室長立替金+${entry.chief_name || '?'}`
      : '口座: 小口現金';
    if (!window.confirm(`このエントリを freee に登録します (${bookDesc} / 部門: ${sections.get(entry.section_id) || entry.section_id} / ${yen(entry.amount)})。よろしいですか?`)) return;
    setBusy(true);
    try {
      const r = await registerPettyCashEntry(entry.id);
      if (r.ok) { flash(`freee 登録完了 (deal ${r.freee_deal_id})`); onChanged(); }
      else flash(`登録失敗: ${r.error || ''}`);
    } catch (e: any) { flash(`登録失敗: ${e?.message || ''}`); }
    finally { setBusy(false); }
  };

  const doReject = async () => {
    const reason = window.prompt('却下理由 (任意):') ?? undefined;
    if (reason === undefined && !window.confirm('却下しますか?')) return;
    setBusy(true);
    try { await rejectPettyCashEntry(entry.id, reason); flash('却下しました'); onChanged(); }
    catch (e: any) { flash(`却下失敗: ${e?.message || ''}`); }
    finally { setBusy(false); }
  };

  const copyT = () => {
    if (!entry.invoice_reg_number) return;
    navigator.clipboard?.writeText(entry.invoice_reg_number);
    flash(`T番号をコピーしました: ${entry.invoice_reg_number} (freee の取引先登録に貼り付けできます)`);
  };

  const photos: Array<{ slot: 'slip' | `r${number}`; label: string }> = [];
  if (entry.has_slip) photos.push({ slot: 'slip', label: '出金伝票' });
  for (let i = 0; i < entry.receipt_count; i++) photos.push({ slot: `r${i}` as `r${number}`, label: `証憑${i + 1}` });
  const pd = entry.proposed_deal;

  return (
    <div style={{ padding: '12px 14px', background: '#fdfdfc', borderTop: `1px solid ${LINE}` }}>
      {/* 写真 */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {photos.map((p) => (
          <button key={p.slot} type="button" onClick={() => setImgView(pettyCashImageUrl(entry.id, p.slot))}
            style={{ cursor: 'zoom-in', border: `1px solid ${LINE}`, borderRadius: 8, background: '#fff', padding: 3, width: 92 }}>
            <img src={pettyCashImageUrl(entry.id, p.slot)} alt={p.label} loading="lazy" style={{ width: '100%', height: 70, objectFit: 'cover', borderRadius: 5 }} />
            <div style={{ fontSize: '0.625rem', color: SUB, marginTop: 2 }}>{p.label}</div>
          </button>
        ))}
      </div>

      {/* AI 提案 */}
      {pd && Array.isArray(pd.details) && (
        <div style={{ border: `1px solid ${LINE}`, borderRadius: 8, overflow: 'hidden', marginBottom: 10, background: '#fff' }}>
          <div style={{ padding: '6px 10px', fontSize: '0.6875rem', fontWeight: 700, color: SUB, background: HEAD_BG, borderBottom: `1px solid ${LINE}` }}>
            AI 仕訳提案 — 発生日 {pd.issue_date} / 口座: 小口現金 / Σ {yen(pd.details.reduce((s: number, d: any) => s + (Number(d.amount) || 0), 0))}
          </div>
          {pd.details.map((d: any, i: number) => (
            <div key={i} style={{ display: 'flex', gap: 12, padding: '6px 10px', fontSize: '0.75rem', borderBottom: i < pd.details.length - 1 ? `1px solid ${LINE}` : 'none', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, minWidth: 110 }}>{d.account_name || accounts.get(Number(d.account_item_id)) || `勘定#${d.account_item_id}`}</span>
              <span style={{ ...numCss, fontWeight: 700 }}>{yen(d.amount)}</span>
              <span style={{ color: SUB }}>{d.tax_name || taxes.get(Number(d.tax_code)) || `税${d.tax_code}`}</span>
              <span style={{ color: SUB }}>{sections.get(Number(d.section_id)) || `部門#${d.section_id}`}</span>
              <span style={{ color: SUB, flex: 1 }}>{d.description || ''}</span>
            </div>
          ))}
        </div>
      )}
      {entry.analysis_note && (
        <div style={{ fontSize: '0.75rem', color: SUB, whiteSpace: 'pre-wrap', marginBottom: 10, padding: '8px 10px', background: '#fff', border: `1px solid ${LINE}`, borderRadius: 8 }}>
          <b style={{ color: INK }}>AI 分析メモ:</b> {entry.analysis_note}
        </div>
      )}
      {entry.partner_unregistered && (
        <div style={{ fontSize: '0.75rem', color: WARN, fontWeight: 700, marginBottom: 10, padding: '8px 10px', background: WARN_BG, borderRadius: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>取引先「{entry.partner_name || '?'}」は freee に未登録です (自動作成はしません)。</span>
          {entry.invoice_reg_number && (
            <button type="button" onClick={copyT} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, border: `1px solid ${WARN}`, borderRadius: 6, background: '#fff', color: WARN, fontWeight: 800, fontSize: '0.6875rem', padding: '3px 8px' }}>
              <MdContentCopy size={12} /> T番号コピー {entry.invoice_reg_number}
            </button>
          )}
        </div>
      )}

      {/* コメント */}
      <div style={{ marginBottom: 8 }}>
        {(comments || []).map((c) => (
          <div key={c.id} style={{ display: 'flex', gap: 8, fontSize: '0.75rem', padding: '5px 0', borderBottom: `1px dashed ${LINE}` }}>
            <span style={{ flex: '0 0 auto', fontWeight: 800, fontSize: '0.625rem', padding: '2px 6px', borderRadius: 4, color: c.kind === 'instruction' ? ACCENT : SUB, background: c.kind === 'instruction' ? ACCENT_BG : '#f1efec' }}>
              {c.kind === 'instruction' ? '指示' : 'メモ'}
            </span>
            <span style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{c.text}</span>
            <span style={{ color: FAINT, fontSize: '0.625rem', whiteSpace: 'nowrap' }}>{(c.created_at || '').slice(5, 16)}</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <textarea value={text} onChange={(e) => setText(e.target.value)}
          placeholder={kind === 'instruction' ? '修正指示 (例: 勘定科目を消耗品費に / 発生日は7/18が正)' : 'コメント'}
          style={{ flex: 1, minWidth: 240, minHeight: 46, border: `1px solid ${RULE}`, borderRadius: 8, padding: '7px 10px', fontSize: '0.8125rem', resize: 'vertical', boxSizing: 'border-box' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ display: 'flex', gap: 5 }}>
            {(['comment', 'instruction'] as const).map((k) => (
              <button key={k} type="button" onClick={() => setKind(k)} style={{
                cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 700, padding: '4px 9px', borderRadius: 6,
                border: `1px solid ${kind === k ? ACCENT : '#e2dfda'}`, background: kind === k ? ACCENT_BG : '#fff', color: kind === k ? ACCENT : SUB,
              }}>{k === 'comment' ? 'メモ' : '修正指示'}</button>
            ))}
          </div>
          <div style={{ fontSize: '0.625rem', color: kind === 'comment' ? WARN : SUB, maxWidth: 150, lineHeight: 1.5 }}>
            {kind === 'comment' ? 'メモは記録のみ (AIは動きません)。直してほしい時は「修正指示」を選択' : '送信するとAIが再分析します'}
          </div>
          {kind === 'instruction' && (
            <label style={{ fontSize: '0.6875rem', color: SUB, display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={learn} onChange={(e) => setLearn(e.target.checked)} /> 学習する
            </label>
          )}
          <Button appearance="primary" small onClick={send} disabled={busy || !text.trim()}>{busy ? <InlineSpinner isLoading /> : '送信'}</Button>
        </div>
      </div>

      {/* 操作 */}
      {entry.status !== 'registered' && entry.status !== 'rejected' && (
        <>
          {editOpen && (
            <ManualEditPanel entry={entry} sections={sections} flash={flash}
              onSaved={onChanged} onClose={() => setEditOpen(false)} />
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button appearance="primary" small onClick={doRegister} disabled={busy || !pd || entry.status === 'analyzing' || entry.status === 'submitted'}>承認して freee 登録</Button>
            <Button appearance="secondary" small onClick={() => setEditOpen((v) => !v)} disabled={busy}>{editOpen ? '手動修正を閉じる' : '手動修正'}</Button>
            <Button appearance="tertiary" small onClick={doReject} disabled={busy}>却下</Button>
            {(!pd || entry.status === 'analyzing' || entry.status === 'submitted') && <span style={{ fontSize: '0.6875rem', color: SUB }}>AI の分析完了後に登録できます (手動修正でも作成可)</span>}
          </div>
        </>
      )}
      {entry.freee_deal_id && (
        <div style={{ marginTop: 8, fontSize: '0.75rem' }}>
          <a href={`https://secure.freee.co.jp/deals#deal_id=${entry.freee_deal_id}`} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT, fontWeight: 700 }}>
            freee で開く #{entry.freee_deal_id}
          </a>
        </div>
      )}

      <ImageLightbox src={imgView} onClose={() => setImgView(null)} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// メインページ
// ---------------------------------------------------------------------------
export const PettyCashPage: React.FC<{ book?: PettyCashBook }> = ({ book = 'petty_cash' }) => {
  const kiosk = kioskSectionFromHash();
  const [tab, setTab] = useState<'ledger' | 'submit' | 'weekly'>('ledger');
  const [sections, setSections] = useState<Map<number, string>>(new Map());
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flash = useCallback((m: string) => {
    setToast(m);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  const storeOptions = useMemo(() => {
    const all = Array.from(sections.entries());
    const ordered = [
      ...STORE_FIRST_ORDER.filter((id) => sections.has(id)).map((id) => [id, sections.get(id)!] as [number, string]),
      ...all.filter(([id]) => !STORE_FIRST_ORDER.includes(id)),
    ];
    return ordered.map(([id, name]) => ({ name, value: String(id) }));
  }, [sections]);

  const [accounts, setAccounts] = useState<Map<number, string>>(new Map());
  const [taxes, setTaxes] = useState<Map<number, string>>(new Map());
  useEffect(() => {
    fetchSectionsMap().then(setSections).catch(() => {});
    fetchAccountItemsMap().then(setAccounts).catch(() => {});
    fetchTaxCodesMap().then(setTaxes).catch(() => {});
  }, []);

  // ---- 出納帳 tab ----
  // 'all' = 全店舗横断表示 (出納帳 tab のみ。提出 tab の店舗セレクタには出さない)
  const [sectionId, setSectionId] = useState<number | 'all'>(3068544);
  const ledgerStoreOptions = useMemo(() => [{ name: '全て', value: 'all' }, ...storeOptions], [storeOptions]);
  const [ledger, setLedger] = useState<PettyCashLedgerResponse | null>(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [floatEdit, setFloatEdit] = useState<string | null>(null);

  const loadLedger = useCallback(async () => {
    setLedgerLoading(true);
    try { setLedger(await fetchPettyCashLedger(sectionId, book)); }
    catch (e: any) { flash(`出納帳の取得に失敗: ${e?.message || ''}`); }
    finally { setLedgerLoading(false); }
  }, [sectionId, book, flash]);
  useEffect(() => { if (tab === 'ledger') loadLedger(); }, [tab, loadLedger]);

  const saveFloat = async () => {
    const v = Number(floatEdit);
    if (!Number.isInteger(v) || v < 0) { flash('定額は0以上の整数で入力してください'); return; }
    if (sectionId === 'all') { flash('定額は店舗を選んでから変更してください'); return; }
    try { await savePettyCashSetting(sectionId, v); setFloatEdit(null); flash('定額を保存しました'); loadLedger(); }
    catch (e: any) { flash(`保存失敗: ${e?.message || ''}`); }
  };

  // ---- 提出 tab ----
  const [subSection, setSubSection] = useState<number>(3068544);

  // ---- 週次 tab ----
  const [weekKey, setWeekKey] = useState<string>('');
  const [weeks, setWeeks] = useState<Array<{ week_key: string; n: number; withdrawn_n: number }>>([]);
  const [weekly, setWeekly] = useState<{ week_key: string; sections: PettyCashWeeklySection[]; grand_total: number } | null>(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);

  const loadWeekly = useCallback(async (wk?: string) => {
    setWeeklyLoading(true);
    try {
      const [w, ws] = await Promise.all([fetchPettyCashWeekly(wk || undefined, book), fetchPettyCashWeeks(book)]);
      setWeekly(w); setWeekKey(w.week_key); setWeeks(ws.weeks);
    } catch (e: any) { flash(`週次の取得に失敗: ${e?.message || ''}`); }
    finally { setWeeklyLoading(false); }
  }, [book, flash]);

  const toggleWeeklyMark = async (sectionIdArg: number, current: boolean) => {
    if (book !== 'chief_advance' || !weekly) return;
    try {
      await markPettyCashWeeklyWithdrawn(weekly.week_key, sectionIdArg, !current);
      flash(!current ? '出金済にマークしました' : '出金済マークを解除しました');
      loadWeekly(weekly.week_key);
    } catch (e: any) { flash(`マーク失敗: ${e?.message || ''}`); }
  };
  useEffect(() => { if (tab === 'weekly') loadWeekly(weekKey || undefined); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const weekOptions = useMemo(() => {
    const set = new Set(weeks.map((w) => w.week_key));
    if (weekKey) set.add(weekKey);
    return Array.from(set).sort().reverse().map((w) => ({ name: `${w} (金曜出金)`, value: w }));
  }, [weeks, weekKey]);

  const statusChip = (e: PettyCashEntry) => {
    const m = STATUS_META[e.status] || STATUS_META.submitted;
    return <span style={{ fontSize: '0.625rem', fontWeight: 800, padding: '2px 7px', borderRadius: 999, color: m.color, background: m.bg, whiteSpace: 'nowrap' }}>{m.label}</span>;
  };

  // キオスク (iOS ホーム画面アイコン起動): 提出専用画面のみ表示 (帳簿は URL の b= で決まる)
  if (kiosk != null) {
    return <KioskView initialSection={kiosk.section} book={kiosk.book} initialChief={kiosk.chief} />;
  }

  return (
    <div style={{ background: 'linear-gradient(180deg, #f8f7f5 0%, #f1efec 100%)', minHeight: '100vh', paddingBottom: '4rem' }}>
      <style>{`
        .pc-row:hover { background: #f3f7fe; }
        .pc-print { display: none; }
        @media print {
          @page { size: A4 portrait; margin: 13mm 12mm; }
          .pc-screen { display: none !important; }
          /* AppShell の chrome (左レール/トップバー/警告バナー/ジョブトレイ/Claudeドック/タブ帯) を除去。
             各部品は pa-no-print を持つが、その print ルールは各ページ CSS 持ち — 本ページ分をここで定義 */
          .pa-no-print { display: none !important; }
          .fm-shell-main { margin-left: 0 !important; }
          .pc-print { display: block !important; color: #1a1a1a; font-family: 'Hiragino Kaku Gothic ProN', 'Yu Gothic', 'Noto Sans JP', sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .pc-print * { box-sizing: border-box; }
          .pc-print .doc-head { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2.2pt solid #1a1a1a; padding-bottom: 6pt; }
          .pc-print .doc-title { font-size: 17pt; font-weight: 800; letter-spacing: 0.12em; }
          .pc-print .doc-title small { display: block; font-size: 8pt; font-weight: 600; letter-spacing: 0.35em; color: #555; margin-bottom: 2pt; }
          .pc-print .doc-corp { text-align: right; font-size: 8.5pt; color: #333; line-height: 1.7; }
          .pc-print .strip { display: flex; gap: 0; margin: 8pt 0 10pt; border: 0.8pt solid #1a1a1a; }
          .pc-print .strip .cell { flex: 1; padding: 5pt 8pt; border-right: 0.5pt solid #999; }
          .pc-print .strip .cell:last-child { border-right: none; }
          .pc-print .strip .lbl { font-size: 7pt; color: #666; letter-spacing: 0.15em; margin-bottom: 1pt; }
          .pc-print .strip .val { font-size: 11pt; font-weight: 700; font-variant-numeric: tabular-nums; }
          .pc-print .strip .cell.hero .val { font-size: 15pt; }
          .pc-print h2 { font-size: 10.5pt; margin: 10pt 0 4pt; padding-left: 6pt; border-left: 3pt solid #1a1a1a; letter-spacing: 0.1em; }
          .pc-print table { width: 100%; border-collapse: collapse; }
          .pc-print th, .pc-print td { border: 0.5pt solid #999; padding: 3.2pt 5pt; font-size: 9pt; }
          .pc-print thead th { background: #f0f0ee !important; font-size: 8pt; letter-spacing: 0.06em; }
          .pc-print .num { text-align: right; font-variant-numeric: tabular-nums; }
          .pc-print .ctr { text-align: center; }
          .pc-print .store { font-weight: 700; }
          .pc-print .kin td { font-size: 8.5pt; }
          .pc-print .tot td { font-weight: 800; border-top: 1.6pt double #1a1a1a; background: #fafaf8 !important; }
          .pc-print .yen-big { font-size: 11pt; font-weight: 800; }
          .pc-print .det-store td { background: #f6f6f4 !important; font-weight: 700; }
          .pc-print .det-sub td { color: #222; }
          .pc-print .foot { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 12pt; }
          .pc-print .note { font-size: 7.5pt; color: #555; line-height: 1.8; max-width: 60%; }
          .pc-print .sign { display: flex; gap: 8pt; }
          .pc-print .sign .box { width: 62pt; }
          .pc-print .sign .box .t { font-size: 7.5pt; text-align: center; border: 0.5pt solid #999; border-bottom: none; padding: 2pt 0; background: #f0f0ee !important; }
          .pc-print .sign .box .s { height: 46pt; border: 0.5pt solid #999; }
          .pc-print .chk { display: inline-block; width: 9pt; height: 9pt; border: 0.8pt solid #444; vertical-align: middle; }
          .pc-print .det-wrap { page-break-inside: auto; }
          .pc-print tr { page-break-inside: avoid; }
        }
      `}</style>
      <div className="pc-screen">
      <Container>
        <div style={{ paddingTop: 18 }}>
          <Breadcrumbs links={[{ title: 'FREEEMAN', url: '/' }, { title: '出納帳' }, { title: BOOK_META[book].title }]} />
        </div>
        <div style={{ marginTop: 4, display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <PageTitle>{BOOK_META[book].title}</PageTitle>
          <span style={{ fontSize: '0.75rem', color: SUB }}>{BOOK_META[book].flow}</span>
        </div>

        {/* タブ */}
        <div style={{ marginTop: 12, display: 'flex', gap: 6 }}>
          {([['ledger', '店舗別出納帳'], ['submit', '伝票・証憑を提出'], ['weekly', '週次出金額']] as const).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)} style={{
              cursor: 'pointer', padding: '9px 18px', borderRadius: '10px 10px 0 0', fontSize: '0.8125rem', fontWeight: 800,
              border: `1px solid ${tab === k ? RULE : 'transparent'}`, borderBottom: 'none',
              background: tab === k ? '#fff' : 'transparent', color: tab === k ? ACCENT : SUB,
            }}>{label}</button>
          ))}
        </div>

        {/* ============ 出納帳 ============ */}
        {tab === 'ledger' && (
          <CardBase>
            <div style={{ padding: '14px 18px', display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap', borderBottom: `1px solid ${LINE}` }}>
              <div>
                <div style={{ fontSize: '0.625rem', color: SUB, fontWeight: 700, marginBottom: 4 }}>店舗</div>
                <SelectBox name="pcsec" value={String(sectionId)} options={ledgerStoreOptions} width="medium"
                  onChange={(e: any) => { setFloatEdit(null); setSectionId(e.target.value === 'all' ? 'all' : Number(e.target.value)); }} />
              </div>
              <Button onClick={loadLedger} disabled={ledgerLoading} IconComponent={MdRefresh} small>{ledgerLoading ? <InlineSpinner isLoading /> : null} 再取得</Button>
              <div style={{ flex: 1 }} />
              {ledger && book === 'petty_cash' && (
                <div style={{ display: 'flex', gap: 18, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '0.75rem', color: SUB }}>
                    {sectionId === 'all' ? '定額合計' : '定額'}{' '}
                    {floatEdit == null ? (
                      <>
                        <b style={{ color: INK, ...numCss }}>{yen(ledger.float_amount)}</b>
                        {sectionId !== 'all' && <button type="button" onClick={() => setFloatEdit(String(ledger.float_amount))} style={{ marginLeft: 6, cursor: 'pointer', border: 'none', background: 'none', color: ACCENT, fontSize: '0.6875rem', fontWeight: 700 }}>変更</button>}
                      </>
                    ) : (
                      <>
                        <input value={floatEdit} onChange={(e) => setFloatEdit(e.target.value)} style={{ width: 90, border: `1px solid ${RULE}`, borderRadius: 6, padding: '3px 7px' }} />
                        <button type="button" onClick={saveFloat} style={{ marginLeft: 4, cursor: 'pointer', border: 'none', background: 'none', color: GOOD, fontWeight: 800, fontSize: '0.6875rem' }}>保存</button>
                        <button type="button" onClick={() => setFloatEdit(null)} style={{ cursor: 'pointer', border: 'none', background: 'none', color: SUB, fontSize: '0.6875rem' }}>取消</button>
                      </>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: SUB }}>未補充支出 <b style={{ color: WARN, ...numCss }}>{yen(ledger.outstanding_total)}</b></div>
                  <div style={{ fontSize: '0.875rem', color: SUB }}>手元残高 <b style={{ color: ledger.balance >= 0 ? GOOD : NEG, fontSize: '1.125rem', ...numCss }}>{yen(ledger.balance)}</b></div>
                  {ledger.unknown_amount_count > 0 && <span style={{ fontSize: '0.6875rem', color: WARN, fontWeight: 700 }}>金額未確定 {ledger.unknown_amount_count} 件 (残高に未反映)</span>}
                </div>
              )}
              {ledger && book === 'chief_advance' && (
                <div style={{ display: 'flex', gap: 18, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '0.875rem', color: SUB }}>未精算 (未出金) <b style={{ color: ledger.outstanding_total > 0 ? WARN : GOOD, fontSize: '1.125rem', ...numCss }}>{yen(ledger.outstanding_total)}</b></div>
                  {ledger.unknown_amount_count > 0 && <span style={{ fontSize: '0.6875rem', color: WARN, fontWeight: 700 }}>金額未確定 {ledger.unknown_amount_count} 件</span>}
                </div>
              )}
            </div>
            {!ledger && <div style={{ padding: 48, textAlign: 'center', color: SUB }}><InlineSpinner isLoading /> 読み込み中…</div>}
            {ledger && ledger.entries.length === 0 && <div style={{ padding: 48, textAlign: 'center', color: FAINT }}>{sectionId === 'all' ? '提出はまだありません' : 'この店舗の提出はまだありません'}</div>}
            {ledger && ledger.entries.map((e) => (
              <div key={e.id} style={{ borderBottom: `1px solid ${LINE}` }}>
                <div className="pc-row" onClick={() => setExpanded((s) => { const n = new Set(s); n.has(e.id) ? n.delete(e.id) : n.add(e.id); return n; })}
                  style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '9px 18px', cursor: 'pointer', flexWrap: 'wrap' }}>
                  <span style={{ ...numCss, color: SUB, fontSize: '0.75rem', width: 76 }}>{e.occurred_on || e.submitted_at.slice(0, 10)}</span>
                  <span style={{ fontWeight: 700, color: INK, fontSize: '0.8125rem', minWidth: 130, flex: 1 }}>
                    {e.partner_name || '(取引先未抽出)'}
                    {e.partner_unregistered && <span title="freee 未登録の取引先" style={{ marginLeft: 5, color: WARN, fontWeight: 900 }}>⚠</span>}
                    {e.chief_name && <span style={{ marginLeft: 6, fontSize: '0.625rem', fontWeight: 800, padding: '2px 7px', borderRadius: 999, color: '#6b21a8', background: '#f5f0fb' }}>{e.chief_name}</span>}
                  </span>
                  {sectionId === 'all' && (
                    <span style={{ fontSize: '0.6875rem', fontWeight: 800, padding: '2px 8px', borderRadius: 999, color: ACCENT, background: ACCENT_BG, whiteSpace: 'nowrap' }}>
                      {sections.get(e.section_id) || `部門#${e.section_id}`}
                    </span>
                  )}
                  <span style={{ ...numCss, fontWeight: 800, color: INK, width: 90, textAlign: 'right' }}>{yen(e.amount)}</span>
                  {statusChip(e)}
                  {e.withdrawn && <span title={`西武出金 突合済 (txn ${e.withdrawal_txn_id})`} style={{ color: GOOD, fontWeight: 900, fontSize: '0.75rem' }}>出金済✓</span>}
                  {(e.comment_count || 0) > 0 && <span style={{ fontSize: '0.6875rem', color: SUB }}>💬{e.comment_count}</span>}
                  <span style={{ fontSize: '0.6875rem', color: FAINT }}>週 {e.week_key}</span>
                </div>
                {expanded.has(e.id) && <EntryDetail entry={e} sections={sections} accounts={accounts} taxes={taxes} onChanged={loadLedger} flash={flash} />}
              </div>
            ))}
          </CardBase>
        )}

        {/* ============ 提出 ============ */}
        {tab === 'submit' && (
          <CardBase>
            <div style={{ padding: '18px 20px' }}>
              <div style={{ fontSize: '0.8125rem', fontWeight: 800, color: INK, marginBottom: 4 }}>出金伝票と証憑を撮影して提出</div>
              <div style={{ fontSize: '0.75rem', color: SUB, marginBottom: 14 }}>
                スマホならカメラが起動します。撮影後は自動で書類向けに補正されます(補正OFFで原本のまま)。写真は伝票+証憑あわせて5枚まで。
              </div>
              <SubmitForm sections={sections} storeOptions={storeOptions} section={subSection} onSectionChange={setSubSection}
                onSubmitted={() => { setSectionId(subSection); setTab('ledger'); }} flash={flash} book={book} />
              <StoreLinkCard storeOptions={storeOptions} flash={flash} book={book} />
            </div>
          </CardBase>
        )}

        {/* ============ 週次出金 ============ */}
        {tab === 'weekly' && (
          <CardBase>
            <div style={{ padding: '14px 18px', display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap', borderBottom: `1px solid ${LINE}` }}>
              <div>
                <div style={{ fontSize: '0.625rem', color: SUB, fontWeight: 700, marginBottom: 4 }}>支払週 (金曜)</div>
                <SelectBox name="pcweek" value={weekKey} options={weekOptions.length ? weekOptions : [{ name: weekKey || '—', value: weekKey || '' }]} width="medium"
                  onChange={(e: any) => { setWeekKey(e.target.value); loadWeekly(e.target.value); }} />
              </div>
              <Button onClick={() => loadWeekly(weekKey)} disabled={weeklyLoading} IconComponent={MdRefresh} small>{weeklyLoading ? <InlineSpinner isLoading /> : null} 再取得</Button>
              <div style={{ flex: 1 }} />
              <Button appearance="tertiary" onClick={() => window.print()} IconComponent={MdPrint}>印刷</Button>
            </div>
            {!weekly && <div style={{ padding: 48, textAlign: 'center', color: SUB }}><InlineSpinner isLoading /> 読み込み中…</div>}
            {weekly && (
              <div style={{ padding: '10px 0 4px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead>
                    <tr>
                      {['店舗', '件数', '出金額', book === 'chief_advance' ? '本部現金出金' : '西武出金'].map((h, i) => (
                        <th key={h} style={{ padding: '7px 18px', fontSize: '0.625rem', fontWeight: 700, color: SUB, borderBottom: `1px solid ${RULE}`, background: HEAD_BG, textAlign: i >= 1 ? 'right' : 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {weekly.sections.map((g) => (
                      <tr key={g.section_id}>
                        <td style={{ padding: '8px 18px', fontWeight: 700, borderBottom: `1px solid ${LINE}` }}>{sections.get(g.section_id) || `部門#${g.section_id}`}</td>
                        <td style={{ padding: '8px 18px', textAlign: 'right', borderBottom: `1px solid ${LINE}`, ...numCss }}>{g.entries.length}{g.unknown_amount > 0 && <span style={{ color: WARN, fontSize: '0.6875rem' }}>（金額未確定{g.unknown_amount}）</span>}</td>
                        <td style={{ padding: '8px 18px', textAlign: 'right', fontWeight: 800, borderBottom: `1px solid ${LINE}`, ...numCss }}>{yen(g.total)}</td>
                        <td style={{ padding: '8px 18px', textAlign: 'right', borderBottom: `1px solid ${LINE}` }}>
                          {book === 'chief_advance' ? (
                            <button type="button" onClick={() => toggleWeeklyMark(g.section_id, g.withdrawn)} title="クリックで出金済/未出金を切替 (本部現金は手動マーク)" style={{
                              cursor: 'pointer', border: `1px solid ${g.withdrawn ? GOOD : RULE}`, borderRadius: 6, padding: '3px 10px',
                              background: g.withdrawn ? GOOD_BG : '#fff', color: g.withdrawn ? GOOD : SUB, fontWeight: 800, fontSize: '0.75rem',
                            }}>{g.withdrawn ? '出金済 ✓' : '未出金 → マーク'}</button>
                          ) : (
                            g.withdrawn
                              ? <span style={{ color: GOOD, fontWeight: 900 }}>出金済 ✓</span>
                              : <span style={{ color: SUB }}>未出金</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {weekly.sections.length === 0 && (
                      <tr><td colSpan={4} style={{ padding: 32, textAlign: 'center', color: FAINT }}>この週の提出はありません</td></tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ padding: '10px 18px', fontWeight: 900, background: '#2a2723', color: '#fff' }}>{BOOK_META[book].weeklyLabel}</td>
                      <td style={{ background: '#2a2723' }} />
                      <td style={{ padding: '10px 18px', textAlign: 'right', fontWeight: 900, background: '#2a2723', color: '#fff', ...numCss }}>{yen(weekly.grand_total)}</td>
                      <td style={{ background: '#2a2723' }} />
                    </tr>
                  </tfoot>
                </table>
                <div style={{ padding: '10px 18px', fontSize: '0.6875rem', color: FAINT }}>
                  {book === 'chief_advance'
                    ? '本部現金からの出金は銀行明細に出ないため、出金済マークは手動です (ボタンで切替)。'
                    : '出金済✓ は西武信金の出金明細と店舗合計が金額一致したとき自動表示 (曖昧な場合はマークしません)。'}
                </div>
              </div>
            )}
          </CardBase>
        )}
      </Container>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, minWidth: 280, maxWidth: '90vw' }}>
          <MessageBlock success>{toast}</MessageBlock>
        </div>
      )}
      </div>

      {/* 印刷: 週次出金指示書 (A4・金種表・明細・受領印つき) */}
      {weekly && (() => {
        const DENOMS = [10000, 5000, 1000, 500, 100, 50, 10, 5, 1];
        const breakdown = (amt: number) => {
          const out: Record<number, number> = {}; let r = Math.max(0, Math.round(amt));
          for (const d of DENOMS) { out[d] = Math.floor(r / d); r %= d; }
          return out;
        };
        // 集計軸: 小口現金=店舗 (店舗の封筒に補充) / 室長立替金=立替者 (室長本人に現金を渡す)
        const isChief = book === 'chief_advance';
        type Grp = { key: string; label: string; entries: PettyCashEntry[]; total: number; withdrawn: boolean };
        let groups: Grp[];
        if (isChief) {
          const byChief = new Map<string, Grp>();
          for (const g of weekly.sections) for (const e of g.entries) {
            const key = e.chief_name || '(立替者未設定)';
            if (!byChief.has(key)) byChief.set(key, { key, label: key, entries: [], total: 0, withdrawn: true });
            const grp = byChief.get(key)!;
            grp.entries.push(e);
            if (e.amount != null) grp.total += e.amount;
            if (!e.withdrawn) grp.withdrawn = false;
          }
          groups = Array.from(byChief.values()).sort((a, b) => a.label.localeCompare(b.label, 'ja'));
        } else {
          groups = weekly.sections.map((g) => ({
            key: String(g.section_id),
            label: sections.get(g.section_id) || `部門#${g.section_id}`,
            entries: g.entries, total: g.total, withdrawn: g.withdrawn,
          }));
        }
        const perGroup = groups.map((g) => ({ g, bd: breakdown(g.total) }));
        const denomTotals: Record<number, number> = {};
        for (const d of DENOMS) denomTotals[d] = perGroup.reduce((s, x) => s + x.bd[d], 0);
        const axisLabel = isChief ? '立替者' : '店舗';
        const fmtD = (d: number) => (d >= 10000 ? '1万' : d >= 1000 ? `${d / 1000}千` : String(d));
        const wkDate = new Date(`${weekly.week_key}T00:00:00`);
        const wkLabel = `${wkDate.getFullYear()}年${wkDate.getMonth() + 1}月${wkDate.getDate()}日 (金)`;
        const source = book === 'chief_advance' ? '本部現金' : '西武信用金庫 現金管理口座';
        return (
        <div className="pc-print">
          <div className="doc-head">
            <div className="doc-title">
              <small>{book === 'chief_advance' ? 'CHIEF ADVANCE REIMBURSEMENT' : 'PETTY CASH REPLENISHMENT'}</small>
              {book === 'chief_advance' ? '室長立替金　週次出金指示書' : '小口現金　週次出金指示書'}
            </div>
            <div className="doc-corp">
              クマガイコーポレーション株式会社<br />
              発行: {new Date().toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
          <div className="strip">
            <div className="cell"><div className="lbl">支払日</div><div className="val">{wkLabel}</div></div>
            <div className="cell"><div className="lbl">出金元</div><div className="val" style={{ fontSize: '9.5pt' }}>{source}</div></div>
            <div className="cell"><div className="lbl">対象</div><div className="val" style={{ fontSize: '9.5pt' }}>金曜12:00締め提出分</div></div>
            <div className="cell"><div className="lbl">{isChief ? '立替者数' : '店舗数'} / 件数</div><div className="val">{groups.length} {isChief ? '名' : '店舗'} / {weekly.sections.reduce((s, g) => s + g.entries.length, 0)} 件</div></div>
            <div className="cell hero"><div className="lbl">出金合計</div><div className="val">{yen(weekly.grand_total)}</div></div>
          </div>

          <h2>{axisLabel}別出金額・金種内訳</h2>
          <table>
            <thead>
              <tr>
                <th style={{ width: '16%' }}>{axisLabel}</th>
                <th className="num" style={{ width: '5%' }}>件数</th>
                <th className="num" style={{ width: '11%' }}>出金額</th>
                {DENOMS.map((d) => <th key={d} className="num" style={{ width: '5.8%' }}>{fmtD(d)}</th>)}
                <th className="ctr" style={{ width: '7%' }}>出金済</th>
                <th className="ctr" style={{ width: '9%' }}>受領印</th>
              </tr>
            </thead>
            <tbody>
              {perGroup.map(({ g, bd }) => (
                <tr key={g.key} className="kin">
                  <td className="store">{g.label}</td>
                  <td className="num">{g.entries.length}</td>
                  <td className="num yen-big">{yen(g.total)}</td>
                  {DENOMS.map((d) => <td key={d} className="num">{bd[d] || ''}</td>)}
                  <td className="ctr">{g.withdrawn ? '✓' : <span className="chk" />}</td>
                  <td />
                </tr>
              ))}
              <tr className="tot kin">
                <td>合計</td>
                <td className="num">{weekly.sections.reduce((s, g) => s + g.entries.length, 0)}</td>
                <td className="num yen-big">{yen(weekly.grand_total)}</td>
                {DENOMS.map((d) => <td key={d} className="num">{denomTotals[d] || ''}</td>)}
                <td /><td />
              </tr>
            </tbody>
          </table>

          <div className="det-wrap">
            <h2>明細</h2>
            <table>
              <thead>
                <tr>
                  <th style={{ width: '18%' }}>{axisLabel}</th>
                  <th style={{ width: '12%' }}>発生日</th>
                  {isChief && <th style={{ width: '16%' }}>店舗</th>}
                  <th>取引先 / 内容</th>
                  <th className="num" style={{ width: '13%' }}>金額</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <React.Fragment key={g.key}>
                    {g.entries.map((e, i) => (
                      <tr key={e.id} className="det-sub">
                        {i === 0 && <td className="store" rowSpan={g.entries.length + 1}>{g.label}</td>}
                        <td className="ctr">{e.occurred_on || '—'}</td>
                        {isChief && <td className="ctr">{sections.get(e.section_id) || `部門#${e.section_id}`}</td>}
                        <td>{e.partner_name || '(取引先未確定)'}{e.note ? <span style={{ color: '#666' }}>　{String(e.note).slice(0, 30)}</span> : null}</td>
                        <td className="num">{yen(e.amount)}</td>
                      </tr>
                    ))}
                    <tr className="det-store">
                      <td colSpan={isChief ? 2 : 1} className="ctr">小計</td>
                      <td className="num" colSpan={2}>{yen(g.total)}</td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="foot">
            <div className="note">
              ・出金済欄: {book === 'chief_advance' ? '本部現金からの支払は銀行明細に現れないため、出金時に □ へチェックし、システムの週次画面でも「出金済」をマークしてください。' : '西武信用金庫の出金明細と店舗合計が一致すると自動で ✓ が付きます。'}<br />
              ・金種内訳は各店舗の封入額の目安です (2千円札は使用しない前提)。<br />
              ・本指示書は FREEEMAN 出納帳 ({book === 'chief_advance' ? '室長立替金' : '小口現金'}・支払週 {weekly.week_key}) から自動生成。
            </div>
            <div className="sign">
              <div className="box"><div className="t">出金担当</div><div className="s" /></div>
              <div className="box"><div className="t">確認</div><div className="s" /></div>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default PettyCashPage;
